<template>
  <v-data-table
    :headers="
      includeDate ? headers : headers.filter((el) => el.value !== 'date')
    "
    :items="breaks"
    hide-default-footer
    :no-data-text="$t('breaksNoBreaks')"
    dense
  >
    <template v-slot:item.date="{ item }">
      <span>{{ item.date | dateFilter }}</span>
    </template>
    <template v-slot:top>
      <v-card-title>
        {{ $t("breaksTitle") }}
        <v-spacer></v-spacer>
        <v-dialog v-model="dialog" persistent max-width="500px">
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" icon>
              <v-icon>mdi-plus-circle</v-icon>
            </v-btn>
          </template>
          <v-card tile>
            <v-card-title>
              <span class="headline">{{ breaksDialogTitle }}</span>
            </v-card-title>
            <v-card-text>
              <v-text-field
                type="time"
                v-model="editedItem.start"
                :label="$t('breaksBegin')"
                dense
                class="mt-4"
                :error-messages="breakStartErrors"
                required
                @input="$v.editedItem.start.$touch()"
                @blur="$v.editedItem.start.$touch()"
              ></v-text-field>
              <v-text-field
                type="number"
                v-model="editedItem.duration"
                :label="$t('breaksDuration')"
                dense
                class="mt-4"
                :error-messages="breakDurationErrors"
                required
                @input="$v.editedItem.duration.$touch()"
                @blur="$v.editedItem.duration.$touch()"
              ></v-text-field>
              <v-menu
                v-model="datepicker"
                :close-on-content-click="true"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="290px"
                class="ml-5"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :label="$t('breaksDate')"
                    name="start_dt"
                    dense
                    class="mt-4"
                    type="text"
                    :value="formattedDate"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  :min="moment.parseZone().format('YYYY-MM-DD')"
                  v-model="editedItem.date"
                  no-title
                  scrollable
                  :locale="$i18n.locale"
                ></v-date-picker>
              </v-menu>
              <small>* {{ $t("validation") }}</small>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="red darken-1" text @click="close">{{
                $t("btnCancel")
              }}</v-btn>
              <v-btn
                color="green darken-1"
                text
                @click="save"
                :disabled="$v.$invalid"
                >{{ $t("btnSave") }}</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog v-model="dialogDelete" max-width="500px">
          <v-card tile>
            <v-card-title class="headline">{{
              $t("breaksDelete")
            }}</v-card-title>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="red darken-1" text @click="closeDelete">{{
                $t("btnNo")
              }}</v-btn>
              <v-btn color="green darken-1" text @click="deleteItemConfirm">{{
                $t("btnYes")
              }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-card-title>
    </template>
    <template v-slot:item.actions="{ item }">
      <v-icon small class="mr-2" @click="editItem(item)">mdi-pencil</v-icon>
      <v-icon small @click="deleteItem(item)">mdi-delete</v-icon>
    </template>
  </v-data-table>
</template>

<script>
import { validationMixin } from "vuelidate";
import { required, minValue } from "vuelidate/lib/validators";
import moment from "moment";

export default {
  name: "BreaksTable",

  props: ["breaks", "includeDate"],

  mixins: [validationMixin],

  data() {
    return {
      datepicker: false,
      dialog: false,
      dialogDelete: false,
      headers: [
        {
          text: this.$i18n.t("breaksBegin"),
          align: "start",
          sortable: false,
          value: "start",
        },
        {
          text: this.$i18n.t("breaksDuration"),
          align: "start",
          sortable: false,
          value: "duration",
        },
        {
          text: this.$i18n.t("breaksDate"),
          align: "start",
          sortable: false,
          value: "date",
        },
        {
          text: this.$i18n.t("breaksManage"),
          align: "end",
          sortable: false,
          value: "actions",
        },
      ],
      editedIndex: -1,
      editedItem: {
        start: "",
        duration: "",
        date: null,
      },
      defaultItem: {
        start: "",
        duration: "",
        date: null,
      },
    };
  },

  validations: {
    editedItem: {
      start: {
        required,
      },
      duration: {
        required,
        minValue: minValue(1),
      },
    },
  },

  computed: {
    breaksDialogTitle() {
      return this.editedIndex === -1
        ? this.$i18n.t("breaksNew")
        : this.$i18n.t("breaksEdit");
    },
    formattedDate() {
      return this.editedItem.date
        ? moment(this.editedItem.date).format("DD.MM.YYYY")
        : "";
    },
    breakStartErrors() {
      const errors = [];
      if (!this.$v.editedItem.start.$dirty) return errors;
      !this.$v.editedItem.start.required &&
        errors.push(this.$i18n.t("validationBreakStart"));
      return errors;
    },
    breakDurationErrors() {
      const errors = [];
      if (!this.$v.editedItem.duration.$dirty) return errors;
      !this.$v.editedItem.duration.required &&
        errors.push(this.$i18n.t("validationBreakDuration"));
      !this.$v.editedItem.duration.minValue &&
        errors.push(this.$i18n.t("validationMinValue"));
      return errors;
    },
  },

  watch: {
    dialog(val) {
      val || this.close();
    },
    dialogDelete(val) {
      val || this.closeDelete();
    },
  },

  filters: {
    dateFilter: (value) => {
      return moment(value).format("DD.MM.YYYY");
    },
  },

  methods: {
    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },
    save() {
      if (this.editedIndex > -1) {
        Object.assign(this.breaks[this.editedIndex], this.editedItem);
      } else {
        this.breaks.push(this.editedItem);
      }
      this.$emit("onChange");
      this.close();
      this.$v.$reset();
    },
    editItem(item) {
      this.editedIndex = this.breaks.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialog = true;
    },
    deleteItem(item) {
      this.editedIndex = this.breaks.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialogDelete = true;
    },
    deleteItemConfirm() {
      this.breaks.splice(this.editedIndex, 1);
      this.$emit("onChange");
      this.closeDelete();
    },
    closeDelete() {
      this.dialogDelete = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },
  },
};
</script>
